// WhatsApp entre amigos (LGPD): a chave "Mostrar meu WhatsApp para amigos", desligada até a pessoa ligar e confirmar
// o celular da conta, e o botão de cada amigo. O número só chega a quem é amigo e também ligou a chave; sem isso, o
// botão mostra o cadeado. Desligar para de mostrar o número na hora; o celular da conta continua guardado.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { type MeuContato, esconderMeuWhatsapp, formatarWhatsapp, lerMeuContato, lerWhatsappDoAmigo, mostrarMeuWhatsapp } from './amigos.ts';
import { ErroDaApi } from './api.ts';
import { avisarNaTela } from './aviso.tsx';
import { celularValido } from './celular.ts';
import type { Sessao } from './conta.ts';
import { CampoCelular } from './FolhaCelular.tsx';

export const FECHADO = 'Liberado quando vocês forem amigos e os dois mostrarem o número';
export const CONSENTIMENTO = 'Seus amigos que também liberarem o WhatsApp vão ver o seu número';

export function IconeWhatsapp({ tamanho = 22 }: { tamanho?: number }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3.5 20.5l1.3-4.1A8.5 8.5 0 1 1 8 19.6z" /><path d="M9 8.5c.2 2.6 3.9 6.3 6.5 6.5l1-1.6-2-1-1 .9c-1-.4-2.3-1.7-2.7-2.7l.9-1-1-2z" />
    </svg>
  );
}
function IconeCadeado() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

// A chave, em Amigos, no perfil e em Configurações. Ligar abre a folha com o número e o consentimento.
export function ChaveWhatsapp({ sessao, aoMudar }: { sessao: Sessao; aoMudar?: (ligado: boolean) => void }) {
  const [c, setC] = useState<MeuContato | null>(null);
  const [folha, setFolha] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    let vivo = true;
    if (!sessao.anonimo) lerMeuContato(sessao).then((x) => { if (vivo) setC(x); }, () => {});
    return () => { vivo = false; };
  }, [sessao.id]);
  if (sessao.anonimo || !c) return null;
  async function desligar() {
    setOcupado(true);
    try {
      setC(await esconderMeuWhatsapp(sessao));
      aoMudar?.(false);
      avisarNaTela('Seu número não aparece mais para os amigos.');
    } catch (e) { avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.'); } finally { setOcupado(false); }
  }
  return (
    <section className="cartao chave-whatsapp" aria-label="WhatsApp entre amigos">
      <div className="linha-chave">
        <span className="icone-whats"><IconeWhatsapp /></span>
        <span className="texto-chave">
          <b id="rotulo-whats">Mostrar meu WhatsApp para amigos</b>
          <small>{c.ligado && c.whatsapp ? `Seu número: ${formatarWhatsapp(c.whatsapp)}` : 'Desligado: ninguém vê o seu número.'}</small>
        </span>
        <button role="switch" aria-checked={c.ligado} aria-labelledby="rotulo-whats" className={'chave-whats' + (c.ligado ? ' on' : '')} disabled={ocupado}
          onClick={() => (c.ligado ? void desligar() : setFolha(true))}>
          <span className="bolinha" />
        </button>
      </div>
      <p className="nota">Seu número só aparece para amigos que também mostram o deles. Desligar para de mostrar o número na hora.</p>
      {folha && <FolhaWhatsapp sessao={sessao} celular={c.whatsapp} aoFechar={() => setFolha(false)} aoLigar={(x) => { setC(x); setFolha(false); aoMudar?.(true); }} />}
    </section>
  );
}

// Com o celular da conta, a folha só confirma o número; conta antiga sem celular informa aqui, e ele vira o celular
// da conta, com as mesmas regras.
function FolhaWhatsapp({ sessao, celular, aoFechar, aoLigar }: { sessao: Sessao; celular: string | null; aoFechar: () => void; aoLigar: (c: MeuContato) => void }) {
  const [numero, setNumero] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function confirmar(ev: React.FormEvent) {
    ev.preventDefault();
    setOcupado(true); setErro(null);
    try { aoLigar(await mostrarMeuWhatsapp(sessao, celular ? undefined : numero)); } catch (e) {
      const msg = e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.';
      if (celular) avisarNaTela(msg); else setErro(msg);
    } finally { setOcupado(false); }
  }
  return createPortal(
    <div className="folha-fundo" onClick={(ev) => { if (ev.target === ev.currentTarget) aoFechar(); }}>
      <form className="folha simples folha-whatsapp" role="dialog" aria-modal="true" aria-labelledby="folha-whats" onSubmit={(ev) => void confirmar(ev)}>
        <h2 id="folha-whats">Mostrar meu WhatsApp</h2>
        {!celular && <CampoCelular valor={numero} aoMudar={(v) => { setNumero(v); setErro(null); }} erro={erro} autoFocus ajuda="Ele vira o celular da sua conta." />}
        <p className="consentimento">{CONSENTIMENTO}{celular ? ' ' + formatarWhatsapp(celular) : ''}.</p>
        <button className="btn1 largo" type="submit" disabled={ocupado || (!celular && !celularValido(numero))}>MOSTRAR MEU NÚMERO</button>
        <button className="btn2" type="button" onClick={aoFechar}>Agora não</button>
      </form>
    </div>,
    document.body,
  );
}

// O botão de cada amigo: com os dois mostrando o número, abre a conversa; sem isso, o cadeado e a regra.
export function BotaoWhatsapp({ sessao, usuario, apelido, liberado }: { sessao: Sessao; usuario: string; apelido: string; liberado: boolean }) {
  const [ocupado, setOcupado] = useState(false);
  if (!liberado) {
    return (
      <button className="whats-amigo fechado" aria-label={`WhatsApp de ${apelido}: ${FECHADO}`} onClick={() => avisarNaTela(FECHADO, 4000)}>
        <IconeWhatsapp /><span className="cadeado-whats"><IconeCadeado /></span>
      </button>
    );
  }
  async function abrir() {
    // A aba abre no toque (o navegador barra janela aberta depois de uma espera) e recebe o endereço quando ele chega.
    const janela = window.open('', '_blank');
    setOcupado(true);
    try {
      const r = await lerWhatsappDoAmigo(sessao, usuario);
      if (janela) { janela.opener = null; janela.location.href = r.link; } else location.href = r.link;
    } catch (e) {
      janela?.close();
      avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
    } finally { setOcupado(false); }
  }
  return (
    <button className="whats-amigo" disabled={ocupado} aria-label={`Conversar com ${apelido} no WhatsApp`} onClick={() => void abrir()}>
      <IconeWhatsapp />
    </button>
  );
}
